import type { FC } from "react"
import { memo } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue } from "recoil"
import styled, { css } from "styled-components"

import { selectedInstallAreaIdState } from "~/containers/Proposal/state/designs/installAreas"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import { fontWeight } from "~/utils/styles"

import { ReactComponent as ArrowDown } from "@svg/arrow-down.svg"
import { ReactComponent as SelectIcon } from "@svg/select-multiple.svg"
import { ReactComponent as TrashIcon } from "@svg/trash-icon.svg"

import { AreaOptions, canEditAreasState } from "./AreaOptions"
import { areasOfPanelsIndexState } from "../state/elements/relations"
import { useDeleteArea } from "../state/elements/transactions/deleteArea"
import { useSelectPanelsInArea } from "../state/elements/transactions/selectPanelsInArea"

const Wrapper = styled(motion.li)`
  background-color: var(--white);
  border-bottom: 1px solid #dddddd;
  overflow: hidden;
`
const AreaLabel = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 10px;
  p {
    color: var(--grey-light-font);
    text-transform: capitalize;
    ${fontWeight(500)}
    font-size: var(--step--2);
    line-height: 17px;
  }

  button {
    &:disabled {
      * {
        cursor: not-allowed;
      }
    }
  }
`
const Left = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  svg {
    width: 10px;
    height: 10px;
    transition: all 0.3s ease;
    transform: rotate(-90deg) translateX(2px);
    path {
      fill: var(--grey-light-font);
    }
  }
  ${({ selected }) =>
    selected &&
    css`
      p,
      svg {
        ${fontWeight(600)}
        color: var(--blue);
      }
      svg {
        transform: rotate(0deg) translateX(2px);
        transition: transform 0.3s ease;
      }
    `}
`
const StyledIcon = styled(TrashIcon)`
  overflow: visible;
  width: 14px;
  height: 14px;
  cursor: pointer;
  * {
    opacity: 1;
  }
  path {
    fill: var(--grey-light-font);
    transition: fill 0.3s ease;
  }
  &:hover {
    path {
      fill: var(--grey-dark);
    }
  }
`

export const AreaListItemInternal: FC<{ id: string; index: number }> = ({
  id,
  index,
}) => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useRecoilState(selectedInstallAreaIdState)
  const isSelected = selectedId === id
  const deleteArea = useDeleteArea(id)
  const selectPanels = useSelectPanelsInArea(id)
  const areasOfPanels = useRecoilValue(areasOfPanelsIndexState)
  const panelsToSelect = areasOfPanels.core.get(id) || new Set()
  const canEditAreas = useRecoilValue(canEditAreasState)
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  return (
    <Wrapper layout>
      <motion.div layout>
        <AreaLabel layout>
          <Left
            onClick={() =>
              selectedId === id ? setSelectedId(null) : setSelectedId(id)
            }
            selected={isSelected}
          >
            <span>
              <ArrowDown />
            </span>
            <p>
              {t(`installArea`)} {index + 1}
            </p>
          </Left>
          <span
            style={{ display: `flex`, gap: `10px` }}
            onClick={
              !currentUserIsDesignUser ? triggerPreventionToast : undefined
            }
          >
            {panelsToSelect.size > 0 && (
              <button title="Select Panels in Area" onClick={selectPanels}>
                <StyledIcon
                  style={{ width: `18px`, height: `18px` }}
                  as={SelectIcon}
                />
              </button>
            )}
            <button disabled={!canEditAreas} onClick={() => deleteArea()}>
              <StyledIcon />
            </button>
          </span>
        </AreaLabel>
      </motion.div>
      <AnimatePresence>{isSelected && <AreaOptions id={id} />}</AnimatePresence>
    </Wrapper>
  )
}

export const AreaListItem = memo(AreaListItemInternal)
